import { Popover, Row, Tag } from 'antd'
import { useState, useMemo } from 'react'
import { Icon } from '@iconify/react'
import '../../tasks.index.css'
import styles from './statusSelector.module.css'

const CAPSULE_STATUS_COLORS = {
    Done: { bg: '#DCFCE7', color: '#166534' },
    'In Progress': { bg: '#DBEAFE', color: '#1E40AF' },
    Pending: { bg: '#FEF3C7', color: '#92400E' },
}

export const getCapsuleStyle = (label) =>
    CAPSULE_STATUS_COLORS[label] || { bg: '#E5E7EB', color: '#374151' }

const StatusSelector = ({
    value,
    onChange,
    name,
    statusType = [],
    updateDetails,
    variant = 'default',
    placeholder = 'Select section',
    disabled = false,
    getPopupContainer,
    // Lets a caller mount this already open — used where the trigger is
    // rendered statically and this component only mounts on click.
    defaultOpen = false,
}) => {
    const [visible, setVisible] = useState(defaultOpen)

    const resolvePopupContainer =
        getPopupContainer ||
        (variant === 'section' || variant === 'capsule'
            ? () => document.body
            : (trigger) => trigger?.parentNode || document.body)

    const handleSelect = (item) => {
        if (disabled) return
        const payload = { label: item.label, value: item.value }
        onChange?.(payload)
        updateDetails?.(name, payload)
        setVisible(false)
    }

    const selectedItem = useMemo(() => {
        if (!statusType?.length) return null
        if (value?.value) {
            return statusType.find((item) => item?.value === value.value) || null
        }
        if (value?.label) {
            return statusType.find((item) => item?.label === value.label) || null
        }
        return null
    }, [statusType, value])

    const sectionContent = (
        <div className={styles.sectionPanel}>
            <div className={styles.sectionPanelInner}>
                <div className={styles.sectionHeader}>{placeholder}</div>
                <div className={styles.sectionScroll}>
                    {statusType.map((item, index) => {
                        const isSelected =
                            item?.value != null && value?.value != null
                                ? String(item.value) === String(value.value)
                                : item?.label != null &&
                                  value?.label != null &&
                                  item.label === value.label

                        return (
                            <div
                                key={
                                    item.value != null
                                        ? String(item.value)
                                        : `${item.label}-${index}`
                                }
                                role="option"
                                aria-selected={isSelected}
                                className={`${styles.sectionItem} ${
                                    isSelected ? styles.sectionItemSelected : ''
                                }`}
                                onClick={() => handleSelect(item)}
                            >
                                <div className={styles.sectionItemRow}>
                                    <span
                                        className={styles.sectionCheckSlot}
                                        aria-hidden
                                    >
                                        {isSelected ? (
                                            <Icon
                                                icon="mdi:check"
                                                width={18}
                                                height={18}
                                                className={styles.sectionCheckIcon}
                                                style={{ color: '#595959' }}
                                            />
                                        ) : null}
                                    </span>
                                    <span className={styles.sectionItemLabel}>
                                        {item.label}
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )

    const content = (
        <div className="flex flex-col gap-1 w-[160px]">
            {statusType.map((item) => {
                const isSelected = item?.label === value?.label

                return (
                    <Row
                        key={item.value}
                        onClick={() => handleSelect(item)}
                        align="middle"
                        justify="space-between"
                        className={`${isSelected ? '#f7f5ff' : 'hover-bg'}`}
                        style={{
                            columnGap: '2px',
                            backgroundColor: isSelected ? '#f7f5ff' : '',
                            cursor: 'pointer',
                            padding: '8px 20px',
                        }}
                    >
                        <Row
                            className="flex items-center gap-2"
                            align="middle"
                            style={{
                                columnGap: '2px',
                            }}
                        >
                            <div style={{ width: 18 }}>
                                {isSelected && (
                                    <Icon
                                        icon="ic:round-check"
                                        height={18}
                                        width={18}
                                        className="svgColor"
                                    />
                                )}
                            </div>
                            <Tag
                                color={item?.color || 'cyan'}
                                style={{
                                    borderRadius: 5,
                                    fontWeight: 500,
                                    padding: '2px 10px',
                                    margin: 0,
                                    fontSize: '13px',
                                }}
                            >
                                {item.label}
                            </Tag>
                        </Row>
                    </Row>
                )
            })}
        </div>
    )

    if (variant === 'section') {
        return (
            <Popover
                content={sectionContent}
                trigger={disabled ? [] : 'click'}
                placement="bottomLeft"
                open={disabled ? false : visible}
                onOpenChange={(open) => {
                    if (disabled) return
                    setVisible(open)
                }}
                arrow={false}
                styles={{
                    root: {
                        paddingTop: 4,
                    },
                    body: {
                        padding: 0,
                        background: 'transparent',
                        boxShadow: 'none',
                    },
                }}
                getPopupContainer={resolvePopupContainer}
            >
                <button
                    type="button"
                    className={`${styles.sectionTrigger} ${
                        disabled ? styles.sectionTriggerDisabled : ''
                    }`}
                    aria-haspopup="listbox"
                    aria-expanded={visible}
                    aria-disabled={disabled}
                    disabled={disabled}
                >
                    <span className={styles.sectionTriggerInner}>
                        <span
                            className={`${styles.sectionTriggerText} ${
                                !selectedItem ? styles.sectionPlaceholder : ''
                            }`}
                        >
                            {selectedItem?.label ?? placeholder}
                        </span>
                        {!disabled ? (
                            <Icon
                                icon={
                                    visible
                                        ? 'mdi:chevron-up'
                                        : 'mdi:chevron-down'
                                }
                                width={20}
                                height={20}
                                className={styles.sectionChevron}
                            />
                        ) : null}
                    </span>
                </button>
            </Popover>
        )
    }

    if (variant === 'capsule') {
        const capsuleLabel = selectedItem?.label || value?.label || ''
        const capsuleStyle = getCapsuleStyle(capsuleLabel)
        const capsuleContent = (
            <div className={styles.capsulePanel}>
                {statusType.map((item, index) => {
                    const isSelected =
                        item?.value != null && value?.value != null
                            ? String(item.value) === String(value.value)
                            : item?.label != null &&
                              value?.label != null &&
                              item.label === value.label
                    const optionStyle = getCapsuleStyle(item?.label)

                    return (
                        <div
                            key={
                                item.value != null
                                    ? String(item.value)
                                    : `${item.label}-${index}`
                            }
                            role="option"
                            aria-selected={isSelected}
                            className={`${styles.capsuleOption} ${
                                isSelected ? styles.capsuleOptionSelected : ''
                            }`}
                            onClick={() => handleSelect(item)}
                        >
                            <span className={styles.capsuleOptionCheck}>
                                {isSelected ? (
                                    <Icon
                                        icon="mdi:check"
                                        width={14}
                                        height={14}
                                    />
                                ) : null}
                            </span>
                            <span
                                className={styles.capsulePill}
                                style={{
                                    background: optionStyle.bg,
                                    color: optionStyle.color,
                                }}
                            >
                                {item.label}
                            </span>
                        </div>
                    )
                })}
            </div>
        )

        return (
            <Popover
                content={capsuleContent}
                trigger={disabled ? [] : 'click'}
                placement="bottomLeft"
                open={disabled ? false : visible}
                onOpenChange={(open) => {
                    if (disabled) return
                    setVisible(open)
                }}
                arrow={false}
                styles={{
                    root: {
                        paddingTop: 4,
                    },
                    body: {
                        padding: 0,
                        background: 'transparent',
                        boxShadow: 'none',
                    },
                }}
                getPopupContainer={resolvePopupContainer}
            >
                <button
                    type="button"
                    className={`${styles.capsuleTrigger} ${
                        disabled ? styles.capsuleTriggerDisabled : ''
                    }`}
                    style={{
                        background: capsuleStyle.bg,
                        color: capsuleStyle.color,
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={visible}
                    aria-disabled={disabled}
                    disabled={disabled}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <span className={styles.capsuleTriggerText}>
                        {capsuleLabel || placeholder}
                    </span>
                </button>
            </Popover>
        )
    }

    return (
        <Popover
            content={content}
            trigger={disabled ? [] : 'click'}
            placement="bottomLeft"
            open={disabled ? false : visible}
            onOpenChange={(open) => {
                if (disabled) return
                setVisible(open)
            }}
            arrow={false}
            styles={{
                root: {
                    minWidth: '230px',
                    width: '230px',
                },
                body: {
                    padding: 0,
                },
            }}
            getPopupContainer={resolvePopupContainer}
        >
            <Tag
                color={selectedItem?.color || 'cyan'}
                style={{
                    borderRadius: 5,
                    fontWeight: 500,
                    padding: '2px 10px',
                    margin: 0,
                    cursor: disabled ? 'default' : 'pointer',
                    opacity: disabled ? 0.75 : 1,
                }}
                onClick={(e) => {
                    e.stopPropagation()
                }}
                onMouseDown={(e) => {
                    e.stopPropagation()
                }}
            >
                {selectedItem?.label || 'Select Status'}
            </Tag>
        </Popover>
    )
}

export default StatusSelector
